import React, { useContext, useEffect, useRef, useState } from 'react';
import LazyTable from 'app/component/lazy-table';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { useAppSelector } from 'app/config/store';
import { AUTHORITIES } from 'app/config/constants';
import { ToastContext } from 'app/context/ToastContext';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
import { useNavigate } from 'react-router-dom';
import { boldNameFormatter } from 'app/shared/util/formatters';
import { formatFromLocalDate } from 'app/shared/util/date-utils';

type LazyTableHandle = {
  getEncodedParams: () => any;
};

const ContractTable: React.FC = () => {
  const [contracts, setContracts] = useState(null);
  const account = useAppSelector(state => state.authentication.account);
  const isAdminOrAssistant = hasAnyAuthority(account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.SUPER_ADMIN, AUTHORITIES.ASSISTANT]);
  const isAdmin = hasAnyAuthority(account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.SUPER_ADMIN]);
  const [isLoading, setIsLoading] = useState(false);
  const [parsedAdditionalHeaderFiltersForLazyTable, setParsedAdditionalHeaderFiltersForLazyTable] = useState(null);
  const { showSuccess, showError } = useContext(ToastContext);
  const tableRef = useRef<LazyTableHandle>(null);
  const [additionalHeaderFilters, setAdditionalHeaderFilters] = useState<any>(() => {
    let saved: any = localStorage.getItem('contractFromToFilterRaw');
    const additional = localStorage.getItem('contractFromToFilterAdditional');

    try {
      saved = saved ? JSON.parse(saved) : null;
    } catch (err) {
      console.warn(err);
      saved = null;
    }

    return saved?.to && saved?.from && additional ? additional : null;
  });
  const [additionalSignatureHeaderFilters, setAdditionalSignatureHeaderFilters] = useState<any>(null);
  const [rawAdditionalHeaderFilters, setRawAdditionalHeaderFilters] = useState<any>(() => {
    const saved = localStorage.getItem('contractFromToFilterRaw');
    const additional = localStorage.getItem('contractFromToFilterAdditional');
    try {
      return saved && additional ? JSON.parse(saved) : null;
    } catch (err) {
      console.warn(err);
    }
    return null;
  });
  const [rawAdditionalSignatureHeaderFilters, setRawAdditionalSignatureHeaderFilters] = useState<any>(null);

  useEffect(() => {
    localStorage.removeItem('contractFromToSignatureFilterRaw');
    localStorage.removeItem('contractFromToSignatureFilterAdditional');
  }, []);
  const navigate = useNavigate();

  // useEffect(() => {
  //   console.log('here');
  //   localStorage.setItem('contractFromToFilterRaw', JSON.stringify(rawAdditionalHeaderFilters));
  // }, [rawAdditionalHeaderFilters]);

  // useEffect(() => {
  //   console.log('here2');
  //   localStorage.setItem('contractFromToFilterAdditional', additionalHeaderFilters);
  // }, [additionalHeaderFilters]);

  // const onGenerateDispatchNotes = () => {
  //   if (contracts?.length > 0) {
  //     setIsLoading(true);
  //     generateDispatchNotes(contracts.map(c => c.id))
  //       .then(response => {
  //         const blob = new Blob([response.data], { type: 'application/zip' });
  //
  //         // Create a link element to trigger the download
  //         const link = document.createElement('a');
  //         link.href = window.URL.createObjectURL(blob);
  //         link.download = 'sprievodky.zip';
  //         link.click();
  //
  //         // Clean up
  //         window.URL.revokeObjectURL(link.href);
  //         setContracts([]);
  //       })
  //       .catch(e => {
  //         console.error(e);
  //         showError('Chyba', e.response?.data?.detail);
  //       })
  //       .finally(() => setIsLoading(false));
  //   }
  // };

  const onSelectionChange = value => {
    setContracts(value);
  };

  const onSearchFromTo = () => {
    let additional = null;
    let raw = null;
    let save = false;

    if (
      (rawAdditionalHeaderFilters?.from && !rawAdditionalHeaderFilters?.to) ||
      (!rawAdditionalHeaderFilters?.from && rawAdditionalHeaderFilters?.to)
    ) {
      showError('Upozornenie', 'Musia byť vybraté obe hodnoty OD a DO na filtrovanie.');
      return;
    } else if (!rawAdditionalHeaderFilters?.from && !rawAdditionalHeaderFilters?.to) {
      setAdditionalHeaderFilters(null);
      save = true;
    } else {
      additional = `${rawAdditionalHeaderFilters.from},${rawAdditionalHeaderFilters.to}`;
      raw = rawAdditionalHeaderFilters;
      setAdditionalHeaderFilters(additional);
      save = true;
    }

    if (save) {
      localStorage.setItem('contractFromToFilterRaw', JSON.stringify(raw));
      localStorage.setItem('contractFromToFilterAdditional', additional);
    }
  };

  const onSearchFromToSignature = () => {
    if (
      (rawAdditionalSignatureHeaderFilters?.fromSignature && !rawAdditionalSignatureHeaderFilters?.toSignature) ||
      (!rawAdditionalSignatureHeaderFilters?.fromSignature && rawAdditionalSignatureHeaderFilters?.toSignature)
    ) {
      showError('Upozornenie', 'Musia byť vybraté obe hodnoty OD a DO na filtrovanie.');
      return;
    } else if (!rawAdditionalSignatureHeaderFilters?.fromSignature && !rawAdditionalSignatureHeaderFilters?.toSignature) {
      setAdditionalSignatureHeaderFilters(null);
    } else {
      setAdditionalSignatureHeaderFilters(
        `${rawAdditionalSignatureHeaderFilters.fromSignature},${rawAdditionalSignatureHeaderFilters.toSignature}`
      );
    }
  };

  const onClickAttachments = data => {
    const tempParams = tableRef.current?.getEncodedParams();
    navigate(`/create-contract/${data.id}?attachments&p=${tempParams}`);
  };

  const parseAdditionalHeaderFiltersForLazyTable = (): any => {
    if (additionalHeaderFilters) {
      if (additionalSignatureHeaderFilters) {
        return additionalHeaderFilters + '|' + additionalSignatureHeaderFilters;
      }
      return additionalHeaderFilters + '|-';
    } else if (additionalSignatureHeaderFilters) {
      return '-|' + additionalSignatureHeaderFilters;
    } else {
      return null;
    }
  };

  useEffect(() => {
    setParsedAdditionalHeaderFiltersForLazyTable(parseAdditionalHeaderFiltersForLazyTable());
  }, [additionalHeaderFilters, additionalSignatureHeaderFilters]);

  return LazyTable({
    ref: tableRef,
    initialSortField: 'id',
    initialSortDirection: 'desc',
    createUrl: '/create-contract',
    serverUrl: '/api/contracts/search',
    exportUrl: '/api/contracts/export',
    editActionAllowed: isAdminOrAssistant,
    infoActionAllowed: true,
    deleteActionAllowed: isAdmin,
    globalFilterFields: ['policyHolder.firstName', 'policyHolder.lastName', 'product.insuranceCompany.name', 'product.name'],
    externalLoading: isLoading,
    additionalHeaderFilters: parsedAdditionalHeaderFiltersForLazyTable,
    columns: [
      { field: 'id', filter: true, header: 'Id', sortable: true },
      { field: 'state.name', filter: true, header: 'Stav', sortable: true },
      { field: 'contractNr', filter: true, header: 'Č. zmluvy', sortable: true },
      { field: 'proposalNr', filter: true, header: 'Č. návrhu', sortable: true },
      {
        field: 'policyHolder.nameFullFormatted',
        filter: true,
        filterField: 'policyHolder.nameFullFormatted',
        header: 'Poistník',
        formatter: (_, v) => boldNameFormatter(v?.policyHolder?.nameFullFormatted),
      },
      {
        field: 'concludedBy.nameFullFormatted',
        filter: true,
        header: 'Sprostredkoval',
        formatter: (_, v) => boldNameFormatter(v?.concludedBy?.nameFullFormatted),
      },
      { field: 'product.insuranceCompany.name', filter: true, header: 'Spoločnosť', sortable: true },
      { field: 'product.name', filter: true, header: 'Produkt', sortable: true },
      {
        field: 'template.payment',
        header: 'Lehotná platba',
      },
      { field: 'template.yearlyPayment', header: 'Ročná platba' },
      { field: 'template.paymentFrequency.name', header: 'Frekvencia' },
      { field: 'dateOfSignature', header: 'Dátum podpisu', formatter: (v) => formatFromLocalDate(v) },
      { field: 'insuranceStartAt', header: 'Dátum začiatku zmluvy', formatter: (v) => formatFromLocalDate(v) },
    ],
    additionalActions: [
      {
        icon: 'pi pi-paperclip',
        tooltip: 'Prílohy',
        onClick: onClickAttachments,
      },
    ],
    // selectedItems: contracts,
    // onSelectionChange: isAdminOrAssistant ? onSelectionChange : null,
    filters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.EQUALS },
      contractNr: { value: null, matchMode: FilterMatchMode.CONTAINS },
      proposalNr: { value: null, matchMode: FilterMatchMode.CONTAINS },
      'policyHolder.nameFullFormatted': { value: null, matchMode: FilterMatchMode.CONTAINS },
      'product.insuranceCompany.name': { value: null, matchMode: FilterMatchMode.CONTAINS },
      'product.name': { value: null, matchMode: FilterMatchMode.CONTAINS },
      'state.name': { value: null, matchMode: FilterMatchMode.CONTAINS },
      'concludedBy.nameFullFormatted': { value: null, matchMode: FilterMatchMode.CONTAINS },
      // representative: { value: null, matchMode: FilterMatchMode.IN },
      // status: { value: null, matchMode: FilterMatchMode.EQUALS },
      // verified: { value: null, matchMode: FilterMatchMode.EQUALS }
    },
    additionalHeaderButtons: (
      <div>
        <div>
          <span>
            <label style={{ marginRight: '1rem' }}>Dátum začiatku zmluvy:</label>
            <InputText
              type={'text'}
              value={rawAdditionalHeaderFilters?.from}
              onChange={e => setRawAdditionalHeaderFilters(prev => ({ ...prev, from: e.target.value }))}
              placeholder="Od (dd.mm)"
              style={{ marginRight: '1rem' }}
            />
            <InputText
              type={'text'}
              value={rawAdditionalHeaderFilters?.to}
              onChange={e => setRawAdditionalHeaderFilters(prev => ({ ...prev, to: e.target.value }))}
              style={{ marginRight: '1rem' }}
              placeholder="Do (dd.mm)"
            />
            <Button style={{ backgroundColor: '#e31818' }} icon="pi pi-search" type="button" onClick={onSearchFromTo}></Button>
          </span>
        </div>
        <div>
          <span style={{ marginTop: '5px' }}>
            <label style={{ marginRight: '2rem' }}>Dátum podpisu:</label>
            <InputText
              type={'text'}
              value={rawAdditionalSignatureHeaderFilters?.fromSignature}
              onChange={e => setRawAdditionalSignatureHeaderFilters(prev => ({ ...prev, fromSignature: e.target.value }))}
              placeholder="Od (dd.mm.rrrr)"
              style={{ marginRight: '1rem' }}
            />
            <InputText
              type={'text'}
              value={rawAdditionalSignatureHeaderFilters?.toSignature}
              onChange={e => setRawAdditionalSignatureHeaderFilters(prev => ({ ...prev, toSignature: e.target.value }))}
              style={{ marginRight: '1rem' }}
              placeholder="Do (dd.mm.rrrr)"
            />
            <Button style={{ backgroundColor: '#e31818' }} icon="pi pi-search" type="button" onClick={onSearchFromToSignature}></Button>
          </span>
        </div>
      </div>
    ),
  });
};

export default ContractTable;
